import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import moment from 'moment';
import {
    StatisticsDateRangeService,
    StatisticsTranslatedDateRangePreset,
} from '@monsido/modules/statistics/services/date-range/statistics-date-range.service';

export type SiteAnalyticsDateRangeValue = [Date | null, Date | null] | null;

/**
 * Range date picker for the site analytics pages. Wraps the PrimeNG
 * datepicker and adds the default time range selections (Last Week,
 * Last Month, Last Quarter, Last Half Year and Last Year) as quick
 * select buttons in the picker footer, like the old UI.
 */
@Component({
    selector: 'mon-site-analytics-date-range-picker',
    standalone: true,
    templateUrl: './date-range-picker.component.html',
    styleUrls: ['./date-range-picker.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        DatePickerModule,
        ButtonModule,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SiteAnalyticsDateRangePickerComponent),
            multi: true,
        },
    ],
})
export class SiteAnalyticsDateRangePickerComponent implements ControlValueAccessor {
    @Input() inputId = '';
    @Input() dataTest: string | undefined;

    @ViewChild(DatePicker) private datePicker: DatePicker | undefined;

    readonly maxSelectableDate = new Date();
    readonly yearRange = `${this.maxSelectableDate.getFullYear() - 100}:${this.maxSelectableDate.getFullYear()}`;
    readonly presets: StatisticsTranslatedDateRangePreset[];

    value: SiteAnalyticsDateRangeValue = null;
    disabled = false;
    activePreset: StatisticsTranslatedDateRangePreset | null = null;

    private onChange: (value: SiteAnalyticsDateRangeValue) => void = () => undefined;
    private onTouched: () => void = () => undefined;

    constructor (
        private changeDetectorRef: ChangeDetectorRef,
        statisticsDateRangeService: StatisticsDateRangeService,
    ) {
        this.presets = statisticsDateRangeService.getTranslatedPresets();
    }

    onRangeChange (value: SiteAnalyticsDateRangeValue): void {
        this.value = value;
        this.updateActivePreset();
        this.onChange(value);
        this.onTouched();
    }

    onPresetSelect (preset: StatisticsTranslatedDateRangePreset): void {
        const { startDate, endDate } = preset.getRange();
        const value: [Date, Date] = [startDate.toDate(), endDate.toDate()];

        this.value = value;
        this.activePreset = preset;
        this.onChange(value);
        this.onTouched();
        this.datePicker?.hideOverlay();
    }

    isPresetActive (preset: StatisticsTranslatedDateRangePreset): boolean {
        return this.activePreset === preset;
    }

    writeValue (value: SiteAnalyticsDateRangeValue): void {
        this.value = value ?? null;
        this.updateActivePreset();
        this.changeDetectorRef.markForCheck();
    }

    registerOnChange (fn: (value: SiteAnalyticsDateRangeValue) => void): void {
        this.onChange = fn;
    }

    registerOnTouched (fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState (isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    /**
     * Highlights the preset whose range matches the current value, like the
     * active state in the old UI. Custom or incomplete ranges match nothing.
     */
    private updateActivePreset (): void {
        const [start, end] = this.value ?? [null, null];

        if (!(start instanceof Date) || !(end instanceof Date)) {
            this.activePreset = null;
            return;
        }

        this.activePreset = this.presets.find((preset) => {
            const { startDate, endDate } = preset.getRange();
            return moment(start).isSame(startDate, 'day') && moment(end).isSame(endDate, 'day');
        }) ?? null;
    }
}
